import { LitElement, html, TemplateResult, css, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Task } from '@lit/task';
import { msg, localized } from '@lit/localize';
import { updateLocale } from './localization/config';
import { renderGoodToKnow } from './templates/goodToKnow';
import { renderInPrice } from './templates/inPrice';
import { renderRemarksAndNews } from './templates/remarksAndNews';
import { renderAccommodationOnTour } from './templates/accommodationOnTour';
import { renderShipInformation } from './templates/shipInformation';
import type {
    ItineraryInfo,
    ItineraryInfoMiddlewareRequest,
    ItineraryInfoMiddlewareResponse,
    contentType,
} from './types/itinerary.types';
import mainStyles from './styles/main.scss?inline';
import { emitError, emitWarning } from './services/helper';
import { fetchItineraryInfoContent } from './services/itinerary-content.service';
import { setInitialUri, triggerSchemaJsonEvent } from './services/schemaOrgJsonLd.service';

/**
 *
 */
@localized()
export class Productinfo extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    public identifier = '';

    @property({ type: String, attribute: 'travel-context' })
    public travelContext = 'ROUNDTRIPS';

    @property({ type: String })
    public intent = '';

    @property({ type: String })
    public lang = '';

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: String, attribute: 'module-title' })
    public moduleTitle = 'Leistungen auf einen Blick';

    @property({ type: String, attribute: 'module-intro' })
    public moduleIntro = 'Inklusive & Exklusive Leistungen';

    @property({ type: String, attribute: 'title-included-in-price' })
    public titleIncludedInPrice = '';

    @property({ type: String, attribute: 'title-not-included-in-price' })
    public titleNotIncludedInPrice = '';

    @property({ type: String, attribute: 'title-good-to-know' })
    public titleGoodToKnow = '';

    @property({ type: String, attribute: 'title-remarks-and-news' })
    public titleRemarksAndNews = '';

    @property({ type: String, attribute: 'title-accomodation-infos' })
    public titleAccomodationInfos = '';

    @property({ type: String, attribute: 'title-ship-infos' })
    public titleShipInfos = '';

    @property({ type: String })
    public order = '';

    @state()
    private _itineraryInfo: ItineraryInfo | null | undefined = null;

    private readonly contentTask = new Task<
        readonly [string, string, string, string, string, string, string],
        ItineraryInfo | null
    >(
        this,
        async () => {
            const itineraryInfo = await this._loadItineraryInfo();

            console.log('Loaded itinerary info:', itineraryInfo);

            if (itineraryInfo && this._isEmptyItineraryInfo(itineraryInfo)) {
                this._itineraryInfo = null;
                return null;
            }

            if (itineraryInfo) {
                this._triggerSchemaJsonLd();
            }

            return itineraryInfo;
        },
        () =>
            [
                this.brand,
                this.tenant,
                this.lang,
                this.currency,
                this.identifier,
                this.travelContext,
                this.intent,
            ] as const
    );

    private _currentBrand: string | null = null;

    private readonly DEFAULT_ORDER: contentType[] = [
        'inPrice',
        'accommodationOnTour',
        'goodToKnow',
        'remarksAndNews',
        'shipInformationText',
    ];

    get _titleIncludedInPrice(): string {
        return this.titleIncludedInPrice || msg('Im Preis inbegriffen');
    }

    get _titleNotIncludedInPrice(): string {
        return this.titleNotIncludedInPrice || msg('Nicht im Preis inbegriffen');
    }

    get _titleGoodToKnow(): string {
        return this.titleGoodToKnow || msg('Gut zu wissen');
    }

    get _titleRemarksAndNews(): string {
        return this.titleRemarksAndNews || msg('Bemerkungen und Neuigkeiten');
    }

    get _titleAccomodationInfos(): string {
        return this.titleAccomodationInfos || msg('Unterkünfte auf der Reise');
    }

    get _titleShipInfos(): string {
        return this.titleShipInfos || msg('Schiffsinformationen');
    }

    /**
     * Handles the component's connection to the DOM.
     * This lifecycle method is called when the component is added to the document.
     * It initializes the locale and fetches itinerary information.
     * @returns void
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();

        console.log('Productinfo connected!');

        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);
    }

    /**
     * Handles updates to the component's properties.
     * @param changedProperties - Map of changed properties with their previous values
     */
    updated(changedProperties: Map<string, unknown>): void {
        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de';
            console.log(`Language changed to: ${newLang}`);
            updateLocale(newLang).then(() => {
                this.requestUpdate();
            });
        }

        // Handle brand changes at runtime
        if (changedProperties.has('brand') && this.brand !== this._currentBrand) {
            console.log(`🔄 Brand changed from "${this._currentBrand}" to "${this.brand}"`);
            // Update data-brand attribute
            this._currentBrand = this.brand;
        }
    }

    /**
     * Helper method to fetch itinerary information. In a real-world scenario, this would likely involve an API call.
     * @returns ItineraryInfo | null | undefined - Returns itinerary information.
     */
    protected async _loadItineraryInfo(): Promise<ItineraryInfo | null> {
        const requiredParams = {
            brand: this.brand,
            tenant: this.tenant,
            lang: this.lang,
            currency: this.currency,
            identifier: this.identifier,
            travelcontext: this.travelContext,
        };

        const missing = Object.entries(requiredParams)
            .filter(([, value]) => !value?.trim())
            .map(([key]) => key);

        if (missing.length > 0) {
            emitWarning(this, 'productinfo-middleware-missing-params', { missing });
            this._itineraryInfo = null;
            return this._itineraryInfo;
        }

        try {
            const request: ItineraryInfoMiddlewareRequest = {
                brand: this.brand,
                tenant: this.tenant,
                lang: this.lang,
                currency: this.currency,
                identifier: this.identifier,
                travelcontext: this.travelContext,
                intent: this.intent,
            };
            const responseData = await fetchItineraryInfoContent(request);
            this._itineraryInfo = this._mapMiddlewareToItineraryInfoContent(responseData);
        } catch (error) {
            emitError(this, 'itinerary-info-middleware-fetch-failed', {
                message: error instanceof Error ? error.message : 'Unknown middleware error',
            });
            this._itineraryInfo = null;
        }

        return this._itineraryInfo;
    }

    private static _normalizeListValue(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
        }

        if (typeof value === 'string') {
            const liMatches = value.match(/<li[^>]*>(.*?)<\/li>/gi);

            if (liMatches && liMatches.length > 0) {
                return liMatches
                    .map((item) => item.replace(/<li[^>]*>|<\/li>/gi, '').trim())
                    .filter((item) => item.length > 0);
            }

            const plainText = value.replace(/<[^>]+>/g, '').trim();
            return plainText ? [plainText] : [];
        }

        return [];
    }

    private _isEmptyItineraryInfo(info: ItineraryInfo): boolean {
        const hasNonEmptyList = (value: unknown): boolean => Productinfo._normalizeListValue(value).length > 0;

        const hasNonEmptyText = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

        const listsEmpty = [info.includedInPrice, info.notIncludedInPrice].every((list) => !hasNonEmptyList(list));

        const textsEmpty = [
            info.goodToKnow,
            info.remarksAndNews,
            info.accommodationOnTour,
            info.shipInformationText,
        ].every((value) => !hasNonEmptyText(value));

        return listsEmpty && textsEmpty;
    }

    private _triggerSchemaJsonLd(): void {
        const eventEntries = this._getEventDataObject();
        const data = eventEntries.reduce<Record<string, { name: string; items: string[] }>>((accumulator, entry) => {
            accumulator[entry.propertyId] = {
                name: entry.name,
                items: entry.items,
            };
            return accumulator;
        }, {});

        triggerSchemaJsonEvent(
            {
                uri: setInitialUri(this.identifier),
                data,
            },
            this
        );
    }

    private _mapMiddlewareToItineraryInfoContent(response: ItineraryInfoMiddlewareResponse): ItineraryInfo {
        const offer = response.roundtripOffer;

        return {
            includedInPrice: Productinfo._normalizeListValue(offer?.includedInPrice),
            notIncludedInPrice: Productinfo._normalizeListValue(offer?.notIncludedInPrice),
            goodToKnow: typeof offer?.goodToKnow === 'string' ? offer.goodToKnow : '',
            remarksAndNews: typeof offer?.remarksAndNews === 'string' ? offer.remarksAndNews : '',
            accommodationOnTour: typeof offer?.accommodationOnTour === 'string' ? offer.accommodationOnTour : '',
            shipInformationText: typeof offer?.shipInformationText === 'string' ? offer.shipInformationText : '',
        };
    }

    private _getEventDataObject(): Array<{ name: string; propertyId: string; items: string[] }> {
        if (!this._itineraryInfo) {
            return [];
        }

        const fieldConfig: Array<{ key: keyof ItineraryInfo; name: string; propertyId: string }> = [
            { key: 'includedInPrice', name: this._titleIncludedInPrice, propertyId: 'inclusive' },
            { key: 'notIncludedInPrice', name: this._titleNotIncludedInPrice, propertyId: 'notInclusive' },
            { key: 'goodToKnow', name: this._titleGoodToKnow, propertyId: 'goodToKnow' },
            { key: 'remarksAndNews', name: this._titleRemarksAndNews, propertyId: 'remarksAndNews' },
            { key: 'accommodationOnTour', name: this._titleAccomodationInfos, propertyId: 'accomodations' },
            { key: 'shipInformationText', name: this._titleShipInfos, propertyId: 'shipInformation' },
        ];

        return fieldConfig
            .map(({ key, name, propertyId }) => {
                const rawValue = this._itineraryInfo?.[key];

                const items = Array.isArray(rawValue)
                    ? rawValue.filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
                    : typeof rawValue === 'string' && rawValue.trim().length > 0
                      ? [rawValue]
                      : [];

                return { name, propertyId, items };
            })
            .filter((entry) => entry.items.length > 0);
    }

    /**
     * Helper to get the template based on the content type.
     * This method maps each content type to its corresponding rendering function.
     * @param templateId contentType
     * @returns TemplateResult | null
     */
    getTemplateById(templateId: contentType): TemplateResult | null {
        const templates: Record<contentType, () => TemplateResult | null> = {
            inPrice: () =>
                renderInPrice({
                    titleIncludedInPrice: this._titleIncludedInPrice,
                    titleNotIncludedInPrice: this._titleNotIncludedInPrice,
                    includedInPrice: this._itineraryInfo?.includedInPrice ?? [],
                    notIncludedInPrice: this._itineraryInfo?.notIncludedInPrice ?? [],
                }),
            notInPrice: () => null, // Not rendering separately as it's included in renderInPrice
            goodToKnow: () =>
                renderGoodToKnow({ title: this._titleGoodToKnow, content: this._itineraryInfo?.goodToKnow }),
            remarksAndNews: () =>
                renderRemarksAndNews({
                    title: this._titleRemarksAndNews,
                    content: this._itineraryInfo?.remarksAndNews,
                }),
            accommodationOnTour: () =>
                renderAccommodationOnTour({
                    title: this._titleAccomodationInfos,
                    content: this._itineraryInfo?.accommodationOnTour,
                }),
            shipInformationText: () =>
                renderShipInformation({
                    title: this._titleShipInfos,
                    content: this._itineraryInfo?.shipInformationText,
                }),
        };

        return templates[templateId] ? templates[templateId]() : null;
    }

    /**
     * Renders the component based on the current state and configuration.
     * @returns TemplateResult - The rendered template.
     */
    private _renderContent(): TemplateResult {
        let finalOrder: contentType[] = [...this.DEFAULT_ORDER];

        if (this.order) {
            const hostOrder = this.order.split(',').map((id) => id.trim()) as contentType[];

            const validHostOrder = hostOrder.filter((id): id is contentType => {
                const isValid = this.DEFAULT_ORDER.includes(id as contentType);
                if (!isValid) {
                    console.warn(`Unknown contentType: ${id}`);
                }
                return isValid;
            });

            const remainingContents = this.DEFAULT_ORDER.filter(
                (id) => !validHostOrder.includes(id as contentType)
            ) as contentType[];

            finalOrder = [...validHostOrder, ...remainingContents];
        }

        return html`
            <div class="productinfo-component">
                <der-ui-module-title title="${this.moduleTitle}" intro="${this.moduleIntro}"></der-ui-module-title>
                ${finalOrder.map((templateId) => this.getTemplateById(templateId))}
            </div>
        `;
    }

    /**
     * Renders the component and keeps itinerary content in sync with task args.
     */
    protected render(): TemplateResult {
        return (
            this.contentTask.render({
                pending: () => html``,
                complete: () => (this._itineraryInfo ? this._renderContent() : html``),
                error: () => html``,
            }) ?? html``
        );
    }
}

if (!customElements.get('der-tm-productinfo')) {
    customElements.define('der-tm-productinfo', Productinfo);
}
